import { useEffect, useMemo, useState } from 'react';
import { ChevronDown, CornerDownLeft, Locate, Pencil, Plus, Route, Search, Target, Trash2, Users, X } from 'lucide-react';
import type { RobotSummary, RobotState } from '@fabricos/protocol-ts';
import { BatteryBar } from '@/components/robot/BatteryBar';
import { CommandPanel } from '@/components/robot/CommandPanel';
import { StatusDot } from '@/components/robot/StatusDot';
import { SwarmPanel } from '@/components/swarm/SwarmPanel';
import { WaypointActionDock } from '@/components/waypoint/WaypointActionDock';
import { useDroneGroups, nextGreekName } from '@/store/droneGroups';
import { useFleet } from '@/store/fleet';
import { useMapMode } from '@/store/mapMode';
import { useSelection } from '@/store/selection';
import { useAssetAliases } from '@/store/assetAliases';
import { useMapPick } from '@/store/mapPick';
import { useParams } from '@/store/params';
import { useToasts } from '@/store/toasts';
import { useTrailPrefs } from '@/store/trailPrefs';
import { recordEvent } from '@/store/appEvents';
import { sendCommand } from '@/hooks/useHubSocket';
import { getActiveMap } from '@/map/context';
import { armConfirm } from '@/lib/armConfirm';
import { cn } from '@/lib/utils';
import { formatFlightMode } from '@/lib/flightMode';
import { AddRobotDialog } from './AddRobotDialog';

const KIND_LABEL: Record<string, string> = {
  air: 'AIR',
  ground: 'GND',
  surface: 'SUR',
  underwater: 'SUB',
};

interface Section {
  /** Group id for explicit groups; null for the implicit remainder section. */
  groupId: string | null;
  name: string;
  members: RobotSummary[];
}

export function FleetSidebar({
  open = false,
  onClose,
  commandDock = true,
}: {
  /** Drawer mode (sub-`lg`) — controls visibility. Ignored at `lg`+. */
  open?: boolean;
  /** Called when the user dismisses the drawer (backdrop click, ESC, close X). */
  onClose?: () => void;
  /** Flight ACTION dock (Arm/Takeoff/Land/…). Pilot only — bench pages like
   *  Assets pass false; arming from a setup screen is a footgun. */
  commandDock?: boolean;
} = {}) {
  const robots = useFleet((s) => s.robots);
  const states = useFleet((s) => s.states);
  const selected = useSelection((s) => s.selected);
  const select = useSelection((s) => s.select);
  const groups = useDroneGroups((s) => s.groups);
  const [query, setQuery] = useState('');
  const [adding, setAdding] = useState(false);

  // ESC closes the drawer when in drawer mode. Static at lg+ → no-op.
  useEffect(() => {
    if (!open || !onClose) return;
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') onClose!();
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  const list = useMemo(() => {
    const q = query.trim().toLowerCase();
    const all = Object.values(robots);
    return q
      ? all.filter(
          (r) =>
            r.id.toLowerCase().includes(q) ||
            r.name.toLowerCase().includes(q),
        )
      : all;
  }, [robots, query]);

  // Sections = explicit groups (creation order), then everything ungrouped
  // under the next free greek letter. A robot in several groups renders in
  // its first group only.
  const sections = useMemo<Section[]>(() => {
    const byId = new Map(list.map((r) => [r.id, r]));
    const seen = new Set<string>();
    const out: Section[] = [];
    const ordered = Object.values(groups).sort((a, b) => a.created_at - b.created_at);
    for (const g of ordered) {
      const members: RobotSummary[] = [];
      for (const id of g.robot_ids) {
        if (seen.has(id)) continue;
        const r = byId.get(id);
        if (!r) continue;
        seen.add(id);
        members.push(r);
      }
      // Keep empty explicit groups visible (unless a filter is active) so
      // they can still be renamed / deleted.
      if (members.length > 0 || !query.trim()) out.push({ groupId: g.id, name: g.name, members });
    }
    const rest = list.filter((r) => !seen.has(r.id));
    if (rest.length > 0) out.push({ groupId: null, name: nextGreekName(groups), members: rest });
    return out;
  }, [groups, list, query]);

  // The action dock at the bottom of the sidebar is contextual to the
  // primary selected robot (single-select). Multi-select → swarm controls only.
  const primarySelectedId = selected.size > 0
    ? (Array.from(selected)[0] as string)
    : null;
  const multiSelect = selected.size > 1;

  return (
    <>
      {/* Backdrop — only rendered under lg when the drawer is open. */}
      <div
        onClick={onClose}
        aria-hidden
        className={cn(
          'fixed inset-0 z-40 bg-black/50 backdrop-blur-[2px] transition-opacity lg:hidden',
          open ? 'opacity-100' : 'pointer-events-none opacity-0',
        )}
      />
      <aside
        className={cn(
          'flex h-full flex-none flex-col border-r border-border-default bg-surface-1',
          // Drawer below lg: fixed, slides in from left of the IconRail edge.
          // At lg+: snaps back into the flex row.
          'fixed inset-y-0 z-40 transition-transform duration-200 ease-out',
          'left-[var(--rail-iconrail)] top-[var(--rail-topbar)]',
          // Built-in -translate-x-full + invisible while closed: arbitrary
          // values like `-translate-x-[110%]` sometimes lose their transform
          // when other classes win specificity; the built-in classes are
          // hard-coded transforms that always apply.
          open
            ? 'visible translate-x-0'
            : 'invisible -translate-x-full',
          'lg:static lg:visible lg:translate-x-0 lg:top-0',
        )}
        style={{ width: 'var(--rail-left)' }}
      >
      {/* Header — title + register + filter */}
      <div className="border-b border-border-default p-3 space-y-2">
        <div className="flex items-center gap-2">
          <span
            className="font-mono text-[10px] font-semibold uppercase tracking-[0.16em]"
            style={{ color: 'var(--color-accent)' }}
          >
            Assets
          </span>
          <span className="font-mono text-[10px] text-fg-secondary">{list.length}</span>
          <div className="ml-auto flex items-center gap-1">
            <button
              onClick={() => setAdding(true)}
              title="Register asset"
              className="grid h-[22px] w-[22px] place-items-center rounded-sm border border-border-default bg-transparent text-fg-secondary hover:bg-surface-2 hover:text-fg-primary"
            >
              <Plus className="h-3 w-3" />
            </button>
            {onClose && (
              <button
                onClick={onClose}
                title="Close fleet panel"
                aria-label="Close"
                className="grid h-[22px] w-[22px] place-items-center rounded-sm border border-border-default bg-transparent text-fg-secondary hover:bg-surface-2 hover:text-fg-primary lg:hidden"
              >
                <X className="h-3 w-3" />
              </button>
            )}
          </div>
        </div>
        <div className="relative">
          <Search className="pointer-events-none absolute left-2 top-1/2 h-[13px] w-[13px] -translate-y-1/2 text-fg-muted/70" />
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="FILTER"
            className={cn(
              'h-7 w-full rounded-sm border border-border-default bg-base pl-7 pr-2 text-[11px]',
              'text-fg-primary placeholder:text-fg-muted',
              'font-mono uppercase tracking-[0.1em] outline-none',
              'focus:border-accent',
            )}
          />
        </div>
      </div>

      {/* Body — assets sectioned by group. overflow-x hidden: overflow-y auto
          alone lets any nowrap row widen the rail into a horizontal scroll. */}
      <div className="flex-1 overflow-y-auto overflow-x-hidden">
        {list.length === 0 ? (
          <EmptyState onAdd={() => setAdding(true)} />
        ) : (
          sections.map((sec) => (
            <div key={sec.groupId ?? '__rest'}>
              <GroupHeader section={sec} />
              {sec.members.map((r) => (
                <AssetRow
                  key={r.id}
                  robot={r}
                  state={states[r.id]}
                  selected={selected.has(r.id)}
                  onSelect={(e) =>
                    select(r.id, { multi: e.shiftKey || e.metaKey || e.ctrlKey })
                  }
                />
              ))}
              {sec.members.length === 0 && (
                <p className="px-3.5 py-1.5 font-mono text-[9.5px] uppercase tracking-wider text-fg-muted">
                  no live members
                </p>
              )}
            </div>
          ))
        )}
      </div>

      {/* Action dock — content depends on map mode:
           - Waypoint: mission controls (upload, run, clear, save/load)
           - Direct (default): per-robot action (Arm/Takeoff/...) or swarm */}
      {commandDock && (
        <FleetSidebarActionDock primarySelectedId={primarySelectedId} multiSelect={multiSelect} />
      )}


      {/* Footer */}
      <div className="border-t border-border-default px-3 py-2 text-[10px] font-mono uppercase tracking-[0.08em] text-fg-muted">
        Shift-click → multi · {selected.size} selected
      </div>

      {adding && <AddRobotDialog onClose={() => setAdding(false)} />}
      </aside>
    </>
  );
}

function SectionLabel({ children }: { children: React.ReactNode }) {
  return (
    <div className="flex items-center gap-2">
      <span
        className="font-mono text-[10px] font-semibold uppercase tracking-[0.16em]"
        style={{ color: 'var(--color-accent)' }}
      >
        {children}
      </span>
      <span className="h-px flex-1" style={{ background: 'var(--color-accent-edge)' }} />
    </div>
  );
}

function FleetSidebarActionDock({
  primarySelectedId,
  multiSelect,
}: {
  primarySelectedId: string | null;
  multiSelect: boolean;
}) {
  const mapMode = useMapMode((s) => s.mode);
  if (mapMode === 'waypoint') {
    return (
      <div className="border-t border-border-default p-3 space-y-3">
        <SectionLabel>Mission</SectionLabel>
        <WaypointActionDock />
      </div>
    );
  }
  // Measure is a read-only tool — no command surface while ranging.
  if (mapMode === 'measure') return null;
  if (!primarySelectedId) return null;
  return (
    <div className="border-t border-border-default p-3 space-y-3">
      <SectionLabel>Action</SectionLabel>
      {multiSelect ? (
        <>
          <SaveGroupButton />
          <SwarmPanel />
        </>
      ) : (
        <CommandPanel robotId={primarySelectedId} />
      )}
    </div>
  );
}

/** Multi-select → one click saves the selection as a group. Unnamed groups
 *  take the next greek letter (Alpha, Beta, …) automatically. */
function SaveGroupButton() {
  const selected = useSelection((s) => s.selected);
  const groups = useDroneGroups((s) => s.groups);
  const create = useDroneGroups((s) => s.create);
  const defaultName = nextGreekName(groups);
  return (
    <button
      type="button"
      onClick={() => {
        // Greek default, no prompt (no blocking dialogs). Rename inline via
        // the pencil on the group header.
        create({ name: defaultName, robot_ids: Array.from(selected) as string[] });
      }}
      className="flex w-full items-center justify-center gap-1.5 rounded border border-dashed border-border-default bg-surface-2/40 px-2 py-1.5 font-mono text-[10.5px] uppercase tracking-[0.12em] text-fg-secondary hover:border-accent hover:bg-accent/5 hover:text-accent"
      title="Save the current selection as a named group"
    >
      <Users className="h-3 w-3" />
      Save as group
      <span className="rounded bg-surface-3 px-1 text-fg-muted">{selected.size}</span>
    </button>
  );
}

function GroupHeader({ section }: { section: Section }) {
  const rename = useDroneGroups((s) => s.rename);
  const remove = useDroneGroups((s) => s.remove);
  const select = useSelection((s) => s.select);
  const explicit = section.groupId !== null;
  // Inline rename — never window.prompt (operator rule: no blocking dialogs).
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(section.name);

  function selectAll() {
    const ids = section.members.map((r) => r.id);
    if (ids.length === 0) return;
    useSelection.getState().selectMany(ids);
  }

  function commitRename() {
    if (draft.trim() && draft.trim() !== section.name) rename(section.groupId!, draft.trim());
    setEditing(false);
  }

  return (
    <div className="group/hdr flex items-center gap-2 px-3 pb-1.5 pt-2.5">
      {editing ? (
        <input
          autoFocus
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onBlur={commitRename}
          onKeyDown={(e) => {
            if (e.key === 'Enter') commitRename();
            if (e.key === 'Escape') setEditing(false);
          }}
          className="w-24 rounded border border-border-default bg-surface-2 px-1 font-mono text-[10px] font-semibold uppercase tracking-[0.16em] text-fg-primary outline-none"
        />
      ) : (
        <button
          type="button"
          onClick={selectAll}
          title={`Select all ${section.members.length} in ${section.name}`}
          className="font-mono text-[10px] font-semibold uppercase tracking-[0.16em] hover:underline"
          style={{ color: 'var(--color-accent)' }}
        >
          {section.name}
        </button>
      )}
      <span className="font-mono text-[10px] text-fg-secondary">{section.members.length}</span>
      <span className="h-[1px] flex-1" style={{ background: 'var(--color-accent-edge)' }} />
      {explicit && (
        <span className="flex items-center gap-0.5 opacity-0 transition-opacity group-hover/hdr:opacity-100">
          <button
            type="button"
            onClick={() => {
              setDraft(section.name);
              setEditing((v) => !v);
            }}
            title="Rename group"
            className="grid h-5 w-5 place-items-center rounded text-fg-muted hover:bg-surface-3 hover:text-fg-primary"
          >
            <Pencil className="h-2.5 w-2.5" />
          </button>
          <button
            type="button"
            onClick={() => {
              if (armConfirm(`rm-group-${section.groupId}`, `Remove group "${section.name}"? Drones stay in the fleet.`)) {
                remove(section.groupId!);
              }
            }}
            title="Delete group (drones are kept)"
            className="grid h-5 w-5 place-items-center rounded text-fg-muted hover:bg-surface-3 hover:text-status-critical"
          >
            <Trash2 className="h-2.5 w-2.5" />
          </button>
        </span>
      )}
    </div>
  );
}

function AssetRow({
  robot,
  state,
  selected,
  onSelect,
}: {
  robot: RobotSummary;
  state: RobotState | undefined;
  selected: boolean;
  onSelect: (e: React.MouseEvent) => void;
}) {
  const domain = (robot.kind.frame as any).domain ?? 'air';
  const kind = KIND_LABEL[domain] ?? domain.toUpperCase();
  const mode = formatFlightMode(state?.mode);
  const followSelected = useSelection((s) => s.followSelected);
  const setFollow = useSelection((s) => s.setFollow);
  const select = useSelection((s) => s.select);
  const isFollowingThis = selected && followSelected;
  const hasPos = !!state?.position;
  const trailHidden = useTrailPrefs((s) => s.hidden[robot.id] === true);
  const trailColor = useTrailPrefs((s) => s.colors[robot.id] ?? '#4fc8e0');
  const [expanded, setExpanded] = useState(false);
  const [rtlAltM, setRtlAltM] = useState(15);

  function applyRtlAlt() {
    // RTL_ALT is centimetres on ArduPilot. Armed-OK per the hub allowlist.
    const known = useParams.getState().byRobot[robot.id]?.['RTL_ALT'];
    sendCommand({
      kind: 'param_set',
      id: `cmd-${Math.random().toString(36).slice(2, 10)}-${Date.now()}`,
      robot_id: robot.id,
      priority: 'normal',
      param_id: 'RTL_ALT',
      value: rtlAltM * 100,
      // 6 = MAV_PARAM_TYPE_INT32 (ArduCopter's RTL_ALT) when the param
      // table hasn't been loaded to tell us; ArduPilot transports values
      // by-float either way.
      param_type: known?.param_type ?? 6,
    });
    useToasts.getState().push({
      tone: 'good',
      title: `RTL altitude → ${rtlAltM} m — ${robot.name}`,
      body: 'RTL_ALT written; verify via the echo in Assets → Params if in doubt.',
      durationMs: 4000,
    });
    recordEvent('pilot', `RTL_ALT set to ${rtlAltM} m for ${robot.name}`, { robotId: robot.id });
  }

  function locate(e: React.MouseEvent) {
    e.stopPropagation();
    if (!state?.position) return;
    // Don't disturb the user's existing selection — Locate is a viewport-only
    // action. Just pan and zoom the map to this asset.
    getActiveMap()?.easeTo({
      center: [state.position.lon, state.position.lat],
      zoom: Math.max(getActiveMap()?.getZoom() ?? 14, 16),
      duration: 500,
    });
  }

  function toggleFollow(e: React.MouseEvent) {
    e.stopPropagation();
    if (!hasPos) return;
    // Following requires single-selection. If this row isn't the sole
    // selection, claim it before turning follow on.
    if (!selected) select(robot.id);
    setFollow(!isFollowingThis);
  }

  function pickRtlPoint(e: React.MouseEvent) {
    e.stopPropagation();
    useMapPick.getState().start(`Set RTL point — ${robot.name}`, (lat, lon) => {
      sendCommand({
        kind: 'set_home',
        id: `cmd-${Math.random().toString(36).slice(2, 10)}-${Date.now()}`,
        robot_id: robot.id,
        priority: 'normal',
        lat,
        lon,
      });
      useToasts.getState().push({
        tone: 'good',
        title: `RTL point set — ${robot.name}`,
        body: `${lat.toFixed(6)}, ${lon.toFixed(6)} (DO_SET_HOME)`,
        durationMs: 5000,
      });
      recordEvent('pilot', `RTL point set for ${robot.name}`, { robotId: robot.id });
    });
  }

  return (
    <div className="border-b border-border-subtle">
    <div
      className={cn(
        'group relative flex w-full items-center gap-3 px-3.5 py-2 transition-colors',
        selected ? 'bg-accent-soft' : 'hover:bg-surface-2',
      )}
    >
      {selected && (
        <span
          className="absolute left-0 top-0 bottom-0 w-[3px]"
          style={{ background: 'var(--color-accent)' }}
          aria-hidden
        />
      )}
      {/* Main hit-area selects the asset. */}
      <button
        type="button"
        onClick={onSelect}
        className="flex min-w-0 flex-1 items-center gap-3 text-left"
      >
        <StatusDot status={robot.connection} />
        <div className="min-w-0 flex-1">
          <div className="flex items-baseline gap-2">
            <span
              className={cn(
                'truncate font-mono text-[13px] tracking-[0.02em]',
                selected ? 'text-accent' : 'text-fg-primary',
              )}
            >
              {robot.name}
            </span>
          </div>
          {/* No hub id anywhere user-facing (operator call 2026-07-14) —
              commands still address robots by id internally. */}
          <div className="mt-0.5 truncate whitespace-nowrap font-mono text-[10px] text-fg-muted">
            <span className="tracking-[0.1em]">{kind}</span> /{' '}
            <span className="uppercase tracking-[0.08em]">{mode}</span>
          </div>
        </div>
      </button>

      {/* Row actions — Locate (one-time pan) + Follow (continuous toggle).
          Visible on row hover or when selected; otherwise hidden so unselected
          rows stay quiet. */}
      <div
        className={cn(
          'flex items-center gap-0.5 transition-opacity',
          selected ? 'opacity-100' : 'opacity-0 group-hover:opacity-100 focus-within:opacity-100',
        )}
      >
        <RowAction
          label={hasPos ? 'Center on this asset' : 'No position yet'}
          onClick={locate}
          disabled={!hasPos}
        >
          <Locate className="h-[13px] w-[13px]" />
        </RowAction>
        <RowAction
          label={
            !hasPos
              ? 'No position yet'
              : isFollowingThis
                ? 'Stop following'
                : 'Follow this asset'
          }
          onClick={toggleFollow}
          disabled={!hasPos}
          active={isFollowingThis}
        >
          <Target className="h-[13px] w-[13px]" />
        </RowAction>
        <RowAction
          label={trailHidden ? "Show this asset's trail" : "Hide this asset's trail"}
          onClick={(e) => {
            e.stopPropagation();
            useTrailPrefs.getState().toggle(robot.id);
          }}
          active={!trailHidden}
        >
          <Route className="h-[13px] w-[13px]" />
        </RowAction>
        <RowAction
          label={expanded ? 'Hide asset options' : 'More asset options (RTL point, trail colour)'}
          onClick={(e) => {
            e.stopPropagation();
            setExpanded((v) => !v);
          }}
          active={expanded}
        >
          <ChevronDown
            className={cn('h-[13px] w-[13px] transition-transform', expanded && 'rotate-180')}
          />
        </RowAction>
      </div>

      <BatteryBar pct={state?.battery?.remaining_pct ?? null} />
    </div>

    {/* Extras — expandable per-asset options (operator ask 2026-07-14).
        Grows downward under the row; more features land here over time. */}
    {expanded && (
      <div className="space-y-1.5 bg-surface-2/40 px-3.5 py-2">
        <div className="flex items-center gap-2">
          <label className="flex flex-1 items-center gap-1.5 font-mono text-[10px] uppercase tracking-wide text-fg-muted">
            Name
            <input
              defaultValue={robot.name}
              key={robot.name}
              onBlur={(e) => useAssetAliases.getState().setAlias(robot.id, e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') (e.target as HTMLInputElement).blur();
              }}
              title="Display name for this asset on this browser"
              className="h-6 min-w-0 flex-1 rounded border border-border-default bg-surface-1 px-1.5 font-mono text-[11px] normal-case tracking-normal text-fg-primary"
            />
          </label>
          <label
            title="Trail colour for this asset"
            className="flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-wide text-fg-muted"
          >
            Trail
            <input
              type="color"
              value={trailColor}
              onChange={(e) => useTrailPrefs.getState().setColor(robot.id, e.target.value)}
              className="h-5 w-7 cursor-pointer rounded border border-border-default bg-transparent p-0"
            />
          </label>
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={pickRtlPoint}
            disabled={!hasPos}
            title="Then click the map — sends DO_SET_HOME (what RTL returns to; defaults to the takeoff spot)"
            className="flex h-6 items-center gap-1.5 rounded border border-border-default bg-surface-1 px-2 font-mono text-[10px] uppercase tracking-wide text-fg-secondary hover:border-accent/60 hover:text-accent disabled:cursor-not-allowed disabled:opacity-40"
          >
            <CornerDownLeft className="h-3 w-3" />
            Set RTL point
          </button>
          <label className="ml-auto flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-wide text-fg-muted">
            RTL alt
            <input
              type="number"
              min={2}
              max={200}
              step={1}
              value={rtlAltM}
              onChange={(e) =>
                setRtlAltM(Math.max(2, Math.min(200, Math.round(Number(e.target.value) || 2))))
              }
              className="h-6 w-14 rounded border border-border-default bg-surface-1 px-1.5 font-mono text-[11px] text-fg-primary"
            />
            m
          </label>
          <button
            type="button"
            onClick={applyRtlAlt}
            title="Write RTL_ALT — the height the drone climbs to before returning (armed-OK param)"
            className="flex h-6 items-center rounded border border-border-default bg-surface-1 px-2 font-mono text-[10px] uppercase tracking-wide text-fg-secondary hover:border-status-good/60 hover:text-status-good"
          >
            SET
          </button>
        </div>
      </div>
    )}
    </div>
  );
}

function RowAction({
  label,
  onClick,
  disabled,
  active,
  children,
}: {
  label: string;
  onClick: (e: React.MouseEvent) => void;
  disabled?: boolean;
  active?: boolean;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      title={label}
      aria-label={label}
      onClick={onClick}
      disabled={disabled}
      className={cn(
        'grid h-6 w-6 place-items-center rounded transition-colors',
        active
          ? 'text-accent bg-accent/15'
          : 'text-fg-muted hover:bg-surface-3 hover:text-fg-primary',
        disabled && 'opacity-40 hover:bg-transparent hover:text-fg-muted cursor-not-allowed',
      )}
    >
      {children}
    </button>
  );
}

function EmptyState({ onAdd }: { onAdd: () => void }) {
  return (
    <div className="flex flex-col items-center justify-center gap-3 px-6 py-10 text-center">
      <p className="text-xs text-fg-muted">No assets registered.</p>
      <p className="text-[11px] text-fg-muted">
        Start a SITL drone or click the + button to register an asset.
      </p>
      <button
        onClick={onAdd}
        className="inline-flex items-center gap-1.5 rounded-sm border border-border-default bg-transparent px-3 py-1.5 text-[11px] text-fg-secondary hover:bg-surface-2 hover:text-fg-primary"
      >
        <Plus className="h-3 w-3" /> Register asset
      </button>
    </div>
  );
}
